<script setup>

import {ref, computed, onMounted, watch} from 'vue';
import StaticTooltip from "@/components/common/other/StaticTooltip";
import Button from "@/components/common/input/Button";
import CheckBox from "@/components/common/input/CheckBox";
import GridFunctionTips from "@/components/common/other/GridFunctionTips";

const emits = defineEmits(['update:currentPaginationRange', 'update:currentPage', 'click-reset',
  'click-save', 'selected-row', 'update:highlightRows', 'sort-column']);

const selectedRowIndex = ref();
const showResetButton = ref(true);

const props = defineProps({

  columns: Array,

  dataCy: String,
  currentPage: {type: Number, default: 1},
  currentPaginationRange: {type: Number},
  pageSize: {type: Number, default: 100},
  allowGridMenu: {type: Boolean, default: false},
  allowPagination: {type: Boolean, default: true},
  selectable: {type: Boolean, default: false},
  height: {type: Number, default: 500},
  width: {type: Number, default: 100},
  showDateIndicator: {type: Boolean, default: true},
  highlightRows: {type: Boolean, default: true},
  allowColumnResize: {type: Boolean, default: false},
  allowColumnSort: {type: Boolean, default: false},
  showTips: {type: Boolean, default: true},

  dataSource: {
    type: Array,
    default: function () {
      return []
    }
  },

})

const sourceColumnIndex = ref(null);
const localData = ref([]);
const pageButtons = ref([]);
const paginationSize = ref(10);
const showGridMenu = ref(false);
const columnMenuToggleColumns = ref(false);
const colResizeStartPosition = ref(0);
const colResizeTopPosition = ref();
const colResizeHeight = ref();
const currentColumnWidth = ref(0);
const columnRef = ref();
const gridWrapperRef = ref();

let previousLeftPos = 0;
let currentColumnMousePosition = 0;
let columnResizeId = '';
let columnResizeLimit = 0;

watch(() => props.dataSource, () => {

  localData.value = JSON.parse(JSON.stringify(props.dataSource));

  createPageButtons();
})

onMounted(() => {

  localData.value = JSON.parse(JSON.stringify(props.dataSource));

  createPageButtons();

})

const customColumns = computed(() => {

  return props.columns.filter(c => c.enabled === true)
});

const gridHeight = computed(() => {

  return props.height + 'px'
})

const wrapperHeight = computed(() => {

  return props.height + 124 + 'px'
})

const pagedData = computed(() => {

  if (localData.value.length > 0) {

    return localData.value.filter((row, index) => {

      // eg if page size set to 10, and current page is 2 (default)
      // currentPage is a v-model to the chosen state class

      let startIndex = (props.currentPage - 1) * props.pageSize; // 1 X 10 = 10   / start = 10
      let endIndex = props.currentPage * props.pageSize; // 2 X 10 = 20   / end = 20

      // if the current index >= 10 (start), and the index is less then 20 (end), return the array from index 10 to 19  = 10 records inclusive (page size!)
      if (index >= startIndex && index < endIndex) {
        return true
      }

    });
  }
})

function rowDataSource(index) {
  return props.dataSource[index];
}

function generateRowData(row, key, column) {

  if (column) {

    // Get the row data from the past in key from the current column
    if (typeof (row[key]) === 'string') {

      if (row[key]) return row[key]

    }

  }

  return row[key];
}

function handleDragStart(e, column) {

  sourceColumnIndex.value = props.columns.findIndex(i => i.id === column.id);

  if (column.isFixed) {

    e.preventDefault();
    return;
  }

}

function handleDragover(e) {

  e.preventDefault();
  return false;
}

function handleDrop(e, destinationColumnData) {

  e.stopPropagation();

  let mainArrayDestinationIndex = props.columns.findIndex(i => i.id === destinationColumnData.id);

  if (sourceColumnIndex.value !== mainArrayDestinationIndex && !destinationColumnData.isFixed) {

    if (mainArrayDestinationIndex < sourceColumnIndex.value) {

      props.columns.splice(mainArrayDestinationIndex, 0, props.columns[sourceColumnIndex.value]);
      props.columns.splice(sourceColumnIndex.value + 1, 1);

    } else {

      props.columns.splice(mainArrayDestinationIndex + 1, 0, props.columns[sourceColumnIndex.value]);
      props.columns.splice(sourceColumnIndex.value, 1);

    }

    // Protection from saving a null column to db, this would cause the app to fail fetching the meta columns for shipments
    let checkColumnArrayFitForSave = true;

    props.columns.forEach(column => {

      if(!column) {

        checkColumnArrayFitForSave = false;

      }
    })

    if (props.allowGridMenu && checkColumnArrayFitForSave) emits('click-save');

  }
}

function setLeftPosition(index) {

  if (customColumns.value[index].isFixed) {
    if (index === 0) return 0;

    let firstColumnDatum = parseInt(customColumns.value[0].width) + 11;
    let width = parseInt(customColumns.value[index - 1].width) + 11

    if (index === 1) return firstColumnDatum + 'px';

    if (index === 2) {

      previousLeftPos = width + firstColumnDatum;
      return width + firstColumnDatum + 'px'
    }

    let currentValue = previousLeftPos;

    previousLeftPos = width + previousLeftPos;

    if (customColumns.value[index].isFixed) return width + currentValue + 'px';

  }

}

function setZ(index) {

  if (customColumns.value[index].isFixed) {

    return 99;
  }

  return 1;

}

function setSelectedRow(column, row, index) {

  if (index === selectedRowIndex.value) {

    row.selectedRow = !row.selectedRow;
    emits('selected-row', props.dataSource[index], index);
    return;
  }

  if (props.selectable) highlightRow(row, index);

  if (!column.isFixed && props.highlightRows) row.selectedRow = !row.selectedRow;

}

function highlightRow(row, index) {

  selectedRowIndex.value = index;

  clearHighlightedRow();

  if (!row.checkRow) emits('selected-row', props.dataSource[index], index);

  row.selectedRow = true;
}

function clearHighlightedRow() {

  for (let ea in localData.value) {
    localData.value[ea]['selectedRow'] = false;
  }

}

function getPaginationPages() {
  // Calc the number of pagination buttons required, eg blocks of pages set in paginationSize (10 default)
  return Math.ceil(pageButtons.value.length / paginationSize.value);

}

function splitPagination() {

  return pageButtons.value.filter((row, index) => {

    let startIndex = (props.currentPaginationRange - 1) * paginationSize.value;
    let endIndex = props.currentPaginationRange * paginationSize.value;

    if (index >= startIndex && index < endIndex) {
      return true
    }

  });

}

function createPageButtons() {

  pageButtons.value = [];

  let roundUp = Math.ceil(localData.value.length / props.pageSize);

  for (let i = 0; i < roundUp; i++) {

    pageButtons.value.push(
        {
          number: i + 1,
          notValid: false
        });
  }

}

function pageUp() {

  if (props.currentPaginationRange < getPaginationPages())
    emits('update:currentPaginationRange', props.currentPaginationRange + 1)
}

function pageDown() {

  if (props.currentPaginationRange > 1) emits('update:currentPaginationRange', props.currentPaginationRange - 1)
}

function handleColumnWidthSave() {

  if (props.allowGridMenu) emits('click-save', true);
}

function dragColumnsMessage(col) {

  if (!col.isFixed) return 'Drag Over Other Colum Headers To Re Order';
  return 'This Column Is Fixed';
}

function resetColumns() {

  // props.columns.forEach(function (item) {
  //   item.enabled = true;
  // })

  props.columns.sort((a, b) => {
    return a.id - b.id
  });
}

function handleMenuDisplay() {

  showGridMenu.value = !showGridMenu.value;

}

function toggleColumns() {

  props.columns.forEach(col => {

    if (!col.isFixed && columnMenuToggleColumns.value) col.enabled = true;
    if (!col.isFixed && !columnMenuToggleColumns.value) col.enabled = false;

  })

}

function copyValue(e, text) {

  e.preventDefault();

  try {

    navigator.clipboard.writeText(text);

  } catch (error) {

    console.log('Needs To Be A HTTPS Secure Site To Use Navigator And Clipboard', error)
  }

}

function setCellColor(column, row) {
  // Return the css color value eg(#345321) held in the row data using the matching prop set in the column array
  if (column.cellColorProp) return row[column.cellColorProp];
}

function setSortOrder(col, descending) {

  props.columns.forEach(col => {

    col.sortDescending = null;

  })

  col.sortDescending = descending;

  emits('sort-column', col);

}

function startResize(e) {

  colResizeStartPosition.value = e.clientX;

  if(colResizeStartPosition.value <= columnResizeLimit) {

    colResizeStartPosition.value = columnResizeLimit;
  }

}

function setColumnResizePosition(e, colIndex, col) {

  col.sortDescending = null;

  currentColumnWidth.value = col.width;
  currentColumnMousePosition = e.clientX;
  columnResizeId = col.id;

  colResizeStartPosition.value = e.clientX;
  colResizeTopPosition.value = columnRef.value[colIndex].getBoundingClientRect().top;
  colResizeHeight.value = gridWrapperRef.value.getBoundingClientRect().height;
  columnResizeLimit = columnRef.value[colIndex].getBoundingClientRect().left -5;

  document.addEventListener('mousemove', startResize);

}

function endResize(e) {

  document.removeEventListener('mousemove', startResize);

  colResizeStartPosition.value = 0;

  let colNewPosition = 0;
  let colMovement = e.clientX - currentColumnMousePosition;
  let colResizeDirection = Math.sign(colMovement);

  if(colMovement !== 0) {

    if(colResizeDirection === 1) colNewPosition = parseInt(currentColumnWidth.value) + colMovement;
    if(colResizeDirection === -1) colNewPosition = parseInt(currentColumnWidth.value) - Math.abs(colMovement);

    let columnToResize = props.columns.find(x => x.id === columnResizeId);

    if(colNewPosition > 50) {

      columnToResize.width = colNewPosition.toString();

      handleColumnWidthSave();

    } else {

      columnToResize.width = '50'

    }

  }

}

function handleResetColumns(){
  showResetButton.value = false;

  setTimeout(() =>  showResetButton.value = true, 2000);

}

</script>

<template>
  <div class="cmp-sticky-grid_wrapper">
    <div v-if="showGridMenu" class="cmp-sticky-grid_menu">
      <div class="cmp-sticky-grid_menu-title">&#9783 Column Configuration
        <div style="font-size: 10px; margin-left: 20px">Add / Remove Columns</div>
        <div class="cmp-sticky-grid_menu-button-area">
          <div v-if="showResetButton" @click="handleResetColumns"
               class="cmp-sticky-grid_menu-button cmp-sticky-grid_reset-button">Reset</div>
          <div v-else @click="emits('click-reset', $event, resetColumns())"
               class="cmp-sticky-grid_menu-button cmp-sticky-grid_confirm-button">Confirm</div>
          <span>
            <div class="global-info-icon"
                 title="Resets the grid to the selections made by the owner, Any column positions will be reset.">
          </div>
          </span>
          <div @click="emits('click-save', $event)"
               title="Save column selections"
               class="cmp-sticky-grid_menu-button cmp-sticky-grid_save-button">Save
          </div>
        </div>
      </div>
      <div class="cmp-sticky-grid_menu-checkbox-area">
        <div style="margin: 0px 30px 20px 0; font-weight: bold">
          <CheckBox
              @change="toggleColumns()"
              v-model="columnMenuToggleColumns"
              :label-width="145"
              label="Toggle">
          </CheckBox>
        </div>
        <div v-for="(column, index) in columns">
          <CheckBox
              v-if="column.isRemovable"
              v-model="column.enabled"
              :label-width="145"
              :label="column.title">
          </CheckBox>
        </div>
      </div>
    </div>
    <div class="cmp-sticky-grid_menu-area">
      <div class="cmp-sticky-grid_grid-title global-center-items">
        <slot name="grid-title"></slot>
      </div>

      <StaticTooltip :icon="false" v-if="props.showTips">
        <template #content>
          <GridFunctionTips></GridFunctionTips>
        </template>
        <div class="global-info-icon"></div>
      </StaticTooltip>
      <div class="cmp-sticky-grid_pagination-area">
        <div v-if="props.allowGridMenu" @click="handleMenuDisplay" class="cmp-sticky-grid_menu-icon"
             title="Column Configuration">&#9776
        </div>
        <div v-if="getPaginationPages() > 0 && props.allowPagination"
             class="cmp-sticky-grid_pages-area" title="Total Pages">Total Pages:
          {{ pageButtons.length }}
        </div>
        <!--        <div v-if="pageButtons.length > 1"> Pages: </div>-->
        <!--        <div v-if="pageButtons.length === 1"> Page: </div>-->
        <div v-if="getPaginationPages() > 1" style="margin: 20px;">
          <StaticTooltip :icon="false">
            <template #content>
              <span v-if="props.currentPaginationRange > 1">Prev Pages</span>
              <span v-else> No Previous Pages</span>
            </template>
            <Button
                :disabled="props.currentPaginationRange === 1"
                style="width: 20px;"
                @click="pageDown()"
                type="toolbar"
                text="<">
            </Button>
          </StaticTooltip>
          <StaticTooltip :icon="false">
            <template #content>
              <span v-if="props.currentPaginationRange !== getPaginationPages()">Next Pages</span>
              <span v-else> No More Pages</span>
            </template>
            <Button
                :disabled="props.currentPaginationRange === getPaginationPages()"
                style="width: 20px;"
                @click="pageUp()"
                type="toolbar"
                text=">">
            </Button>
          </StaticTooltip>
        </div>
        <div v-for="(button, index) in splitPagination()" :key="index">
          <Button
              @click="$emit('update:currentPage', button.number)"
              :title="`Page ${button.number}`"
              style="width: 30px;"
              :class="`${button.number === props.currentPage && 'cmp-sticky-grid_active-page'}`"
              type="toolbar"
              :text="button.number.toString()">
          </Button>
        </div>
      </div>
      <slot name="right-content"></slot>
    </div>
    <div class="cmp-sticky-grid" ref="gridWrapperRef">
      <table>
        <tr class="cmp-sticky-grid_table-header-row">
          <th
              ref="columnRef"
              @dragstart="handleDragStart($event, col)"
              @dragover="handleDragover($event)"
              @drop="handleDrop($event, col)"
              @contextmenu="$event.preventDefault()"
              :style="{minWidth: col.width + 'px', left: setLeftPosition(index), zIndex: setZ(index) }"
              :data-cy="dataCy + 'ColumnIndex' + index"
              :class="{'cmp-sticky-grid_sticky' : col.isFixed, 'cmp-sticky-grid_column-resizing': colResizeStartPosition > 0}"
              draggable="false" v-for="(col, index) of customColumns">{{ col.title }}
            <div draggable="true" :title="dragColumnsMessage(col)" class="cmp-sticky-grid_column-drag-handle"></div>
            <span v-if="col.key === 'selectedRow'" class="cmp-data-grid_select-all-rows">
          <slot name="selectAllRows"></slot></span>
            <div
                v-if="!col.isFixed && allowColumnResize"
                title="Left Click And Drag To Set Column Width"
                @mousedown="setColumnResizePosition($event, index, col)"
                class="cmp-sticky-grid_column-resizer-handle">
            </div>
            <span v-if="!col.isFixed && allowColumnSort" class="cmp-sticky-grid_column-sort-icons">
              <div :class="{'cmp-sticky-grid_sort-active' : col.sortDescending === false}" title="Sort Ascending"
                   class="cmp-sticky-grid_sort-asc" @click="setSortOrder(col, false)"> &#128897;
              </div>
              <div :class="{'cmp-sticky-grid_sort-active' : col.sortDescending === true}" title="Sort Descending"
                   class="cmp-sticky-grid_sort-desc" @click="setSortOrder(col, true)">&#128899;
               </div>
            </span>
          </th>
        </tr>
        <div v-if="localData.length === 0" style="height: 80px;">
          <div class="cmp-sticky-grid_no-data"><i class="fa-solid fa-database"></i> No Data</div>
        </div>
        <tr v-for="(row, rowIndex) in pagedData" :key="rowIndex" class="cmp-sticky-grid_table-data-row">
          <template v-for="(col, index) of customColumns">
            <td
                v-if="typeof $slots[col.key] !== 'undefined'"
                @click="setSelectedRow(col, row, rowIndex)"
                @contextmenu="$event.preventDefault()"
                :style="{maxWidth: col.width + 'px', left: setLeftPosition(index)}"
                :class="{'cmp-sticky-grid_sticky' : col.isFixed,'cmp-sticky-grid_selected' : row.selectedRow}">
              <slot :name="col.key" :column="col" :grid="row" :rowData="rowDataSource(rowIndex)"
                    :rowIndex="rowIndex"></slot>
            </td>
            <td v-else :class="{'cmp-sticky-grid_sticky' : col.isFixed, 'cmp-sticky-grid_selected' : row.selectedRow}"
                @click="setSelectedRow(col, row, rowIndex)"
                @contextmenu="copyValue($event, generateRowData(row, col.key, col))"
                :data-cy="dataCy + 'RowIndex' + index"
                :title="generateRowData(row, col.key)"
                :style="{maxWidth: col.width + 'px', left: setLeftPosition(index), background: setCellColor(col, row)}">
              {{ generateRowData(row, col.key, col) }}
            </td>
          </template>
        </tr>
      </table>
    </div>
    <div
        class="cmp-sticky-grid_column-resizer"
        :style="{left: colResizeStartPosition + 'px', top: colResizeTopPosition + 'px', height: colResizeHeight -10 + 'px'}"
        @mouseup="endResize">
    </div>
  </div>
</template>

<style lang="scss" scoped>

table {
  border-spacing: 0;
  font-size: 12px;
  width: 100%;
}

.cmp-sticky-grid {
  position: relative;
  max-height: v-bind(gridHeight);
  overflow: scroll;
  border: 1px solid #c8c8c8;
  border-top: none;
  scrollbar-width: thin;

  &_wrapper {
    position: relative;
    height: v-bind(wrapperHeight);
  }

  &_table-header-row th {
    position: sticky;
    top: 0;
    border-bottom: 1px solid #dbdbdb;
    border-right: 1px solid #dbdbdb;
    padding: 5px;
    background: var(--grid-header);
    color: #fcfcfc;
    text-align: left;
    height: 30px;
    font-weight: normal;
    overflow: hidden;
    white-space: nowrap;
  }

  &_table-data-row td {
    border-bottom: 1px solid #dbdbdb;
    border-right: 1px solid #dbdbdb;
    padding-left: 5px;
    height: 28px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &_sticky {
    position: sticky;
    box-shadow: 0px 1px 6px -1px #888888;
    background: #fafafa;
  }

  &_table-data-row:nth-child(even) {
    background: #f5f5f5;
  }

  &_table-data-row:nth-child(odd) {
    background: #ffffff;
  }

  &_selected {
    background-image: linear-gradient(180deg, #fffaed, #f6ebc5) !important;
  }

  &_menu-area {
    display: flex;
    padding: 6px;
    //background-image: linear-gradient(90deg, #f5f5f5, #ffffff);
    background-image: linear-gradient(90deg, #efefef, #fafafa);
    border: 1px solid #e0e0e0;
    border-radius: 2px;
    height: 22px;
    margin-bottom: 2px;
  }

  &_pagination-area {
    display: flex;
    align-items: center;
    font-size: 12px;
    margin-left: 10px;
  }

  &_pages-area {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 8px;
    height: 22px;
    //background-color: #f1fff2;
    background-color: #fffded;
    margin-right: 10px;
    font-size: 11px;
    color: #000000;
    border-radius: 4px;
    border: 1px solid #eae1b6;
  }

  &_active-page {
    background-image: none !important;
    background-color: white;
    font-weight: bold;
    color: black !important;
    box-shadow: 1px 1px 2px 0px #484848 !important;
  }

  &_menu-icon {
    margin-right: 30px;
    font-size: 18px;
    cursor: pointer;
  }

  &_column-menu {
    position: absolute;
    top: 25px;
    left: 3px;
  }

  &_column-slider {
    -webkit-appearance: none;
    height: 8px;
    border-radius: 2px;
    background: #e9e9e9;
  }

  &_menu {
    position: absolute;
    top: 36px;
    z-index: 9999;
    width: 280px;
    border: 1px solid #dddddd;
    border-top: 0;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    background-image: linear-gradient(0deg, #f6f6f6, #ffffff);
    box-shadow: 0px 1px 2px 0px #888888;
  }

  &_menu-title {
    position: absolute;
    //margin-left: 6px;
    left: 40px;
    font-size: 14px;
    padding: 0 20px 20px 20px;
    height: 300px;
    width: 160px;
  }

  &_menu-button-area {
    margin-top: 15px;
    display: flex;
    gap: 18px;
    font-size: 20px;
  }

  &_menu-checkbox-area {
    height: 300px;
    overflow-y: auto;
    margin-top: 100px;
    padding-left: 30px;
  }

  &_no-data {
    position: absolute;
    top: 60px;
    left: 900px;
    font-size: 16px;
    color: #9d9d9d;
  }

  &_column-resizer, &_column-resizer-handle {
    cursor: w-resize;
    z-index: 99;
    width: 3px;
  }

  &_column-resizer {
    position: fixed;
    border-right: 1px dotted #ff0000;
  }

  &_column-resizer-handle {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
  }

  &_column-resizing {
    cursor: w-resize;
  }

  &_column-drag-handle {
    position: absolute;
    top: 0;
    left: 5px;
    height: 34px;
    width: 70%;
  }

  &_column-sort-icons {
    float: right;
    //font-size: 14px;
    height: 10px
  }

  &_sort-asc, &_sort-desc {
    color: #bbbbbb;
    height: 5px;
  }

  &_sort-asc {
    margin-top: -2px;
  }

  &_sort-asc:hover, &_sort-desc:hover {
    color: #e1b307;
  }

  &_sort-active {
    color: #fd0000;
  }
}

</style><style lang="scss" scoped>

table {
  border-spacing: 0;
  font-size: 12px;
  width: 100%;
}

.cmp-sticky-grid {
  position: relative;
  max-height: v-bind(gridHeight);
  overflow: scroll;
  border: 1px solid #c8c8c8;
  border-top: none;
  scrollbar-width: thin;

  &_wrapper {
    position: relative;
    height: v-bind(wrapperHeight);
  }

  &_table-header-row th {
    position: sticky;
    top: 0;
    border-bottom: 1px solid #dbdbdb;
    border-right: 1px solid #dbdbdb;
    padding: 5px;
    background: var(--grid-header);
    color: #fcfcfc;
    text-align: left;
    height: 30px;
    font-weight: normal;
    overflow: hidden;
    white-space: nowrap;
  }

  &_table-data-row td {
    border-bottom: 1px solid #dbdbdb;
    border-right: 1px solid #dbdbdb;
    padding-left: 5px;
    height: 28px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &_sticky {
    position: sticky;
    box-shadow: 0px 1px 6px -1px #888888;
    background: #fafafa;
  }

  &_table-data-row:nth-child(even) {
    background: #f5f5f5;
  }

  &_table-data-row:nth-child(odd) {
    background: #ffffff;
  }

  &_selected {
    background-image: linear-gradient(180deg, #fffaed, #f6ebc5) !important;
  }

  &_menu-area {
    display: flex;
    padding: 6px;
    //background-image: linear-gradient(90deg, #f5f5f5, #ffffff);
    background-image: linear-gradient(90deg, #efefef, #fafafa);
    border: 1px solid #e0e0e0;
    border-radius: 2px;
    height: 22px;
    margin-bottom: 2px;
  }

  &_pagination-area {
    display: flex;
    align-items: center;
    font-size: 12px;
    margin-left: 10px;
  }

  &_pages-area {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 8px;
    height: 22px;
    //background-color: #f1fff2;
    background-color: #fffded;
    margin-right: 10px;
    font-size: 11px;
    color: #000000;
    border-radius: 4px;
    border: 1px solid #eae1b6;
  }

  &_active-page {
    background-image: none !important;
    background-color: white;
    font-weight: bold;
    color: black !important;
    box-shadow: 1px 1px 2px 0px #484848 !important;
  }

  &_menu-icon {
    margin-right: 30px;
    font-size: 18px;
    cursor: pointer;
  }

  &_column-menu {
    position: absolute;
    top: 25px;
    left: 3px;
  }

  &_column-slider {
    -webkit-appearance: none;
    height: 8px;
    border-radius: 2px;
    background: #e9e9e9;
  }

  &_menu {
    position: absolute;
    top: 36px;
    z-index: 9999;
    width: 280px;
    border: 1px solid #dddddd;
    border-top: 0;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    background-image: linear-gradient(0deg, #f6f6f6, #ffffff);
    box-shadow: 0px 1px 2px 0px #888888;
  }

  &_menu-title {
    position: absolute;
    //margin-left: 6px;
    left: 40px;
    font-size: 14px;
    padding: 0 20px 20px 20px;
    height: 300px;
    width: 160px;
  }

  &_menu-button-area {
    margin-top: 15px;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 20px;
  }

  &_menu-checkbox-area {
    height: 300px;
    overflow-y: auto;
    margin-top: 100px;
    padding-left: 30px;
  }

  &_no-data {
    position: absolute;
    top: 60px;
    left: 900px;
    font-size: 16px;
    color: #9d9d9d;
  }

  &_column-resizer, &_column-resizer-handle {
    cursor: w-resize;
    z-index: 99;
    width: 3px;
  }

  &_column-resizer {
    position: fixed;
    border-right: 1px dotted #ff0000;
  }

  &_column-resizer-handle {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
  }

  &_column-resizing {
    cursor: w-resize;
  }

  &_column-drag-handle {
    position: absolute;
    top: 0;
    left: 5px;
    height: 34px;
    width: 70%;
  }

  &_column-sort-icons {
    float: right;
    //font-size: 14px;
    height: 10px
  }

  &_sort-asc, &_sort-desc {
    color: #bbbbbb;
    height: 5px;
  }

  &_sort-asc {
    margin-top: -7px;
    margin-bottom: 5px;
  }

  &_sort-asc:hover, &_sort-desc:hover {
    color: #e1b307;
  }

  &_sort-active {
    color: #fd0000;
  }

  &_menu-button {
    font-size: 11px;
    height: 20px;
    width: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    border-radius: 3px;
    cursor: pointer;
  }

  &_menu-button:hover {
    opacity: 0.8;
  }

  &_save-button {
    background: #0e74b9;
    //width: 40px;
    margin-left: 50px;
  }

  &_reset-button {
    background:#ffbe42;
    margin-left: -30px;
  }

  &_confirm-button {
    background: #34a459;
    margin-left: -30px;
  }
}

</style>
